import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../CustomCodes.redux';
import { Button, FormControlLabel, FormLabel, Radio, RadioGroup, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import customCodesSx from '../../CustomCodes.styles';
import Info from 'components/Info';

const SimpleCode = () => {
    const theme = useTheme(),
        sx = customCodesSx(theme),
        dispatch = useDispatch(),
        category = useSelector((state) => state.customCodes.category),
        codeName = useSelector((state) => state.customCodes.conf.symbol_name),
        abbreviation = useSelector((state) => state.customCodes.conf.symbol_abbreviation),
        description = useSelector((state) => state.customCodes.conf.symbol_description),
        mapTo = useSelector((state) => state.customCodes.conf.map_to),
        saving = useSelector((state) => state.customCodes.saving);

    const setCodeProperty = (property) => (e) => {
        dispatch(actions.setCodeProperty(property, e.target.value));
    };

    // Set up default info if abbreviation is undefined
    useEffect(() => {
        if (codeName === undefined) {
            dispatch(
                actions.setState({
                    conf: {
                        symbol_abbreviation: '',
                        symbol_name: '',
                        symbol_description: '',
                        symbol_data_type: 'text',
                        symbol_category: category,
                        is_approved: false,
                        map_to: 'tooth',
                        symbol_chart_config: {
                            type: 'single',
                            render: {
                                type: 'text',
                                position: 'user',
                                scaleType: 'default',
                                scaleValue: 1,
                                defaultValue: '',
                            },
                            attachment: 'tooth',
                        },
                    },
                })
            );
        }
    }, [dispatch, codeName, category]);

    // Don't render until initial setup is finished
    if (codeName === undefined) {
        return null;
    }

    return (
        <div>
            <div style={sx.textInputWrapper}>
                <TextField
                    value={codeName}
                    label="Custom Code Name"
                    sx={sx.textInput}
                    variant="outlined"
                    onChange={setCodeProperty('symbol_name')}
                    slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { maxLength: 80 },
                        input: {
                            endAdornment: (
                                <Info>
                                    <div>The full name of your code. This will appear on reports.</div>
                                </Info>
                            ),
                        },
                    }}
                />
            </div>

            <div style={sx.textInputWrapper}>
                <TextField
                    value={abbreviation}
                    label="Abbreviation"
                    sx={sx.textInput}
                    variant="outlined"
                    onChange={setCodeProperty('symbol_abbreviation')}
                    slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { maxLength: 8 },
                        input: {
                            endAdornment: (
                                <Info>
                                    <div>The abbreviation of your code. This will appear on charts.</div>
                                </Info>
                            ),
                        },
                    }}
                />
            </div>

            <div style={sx.textInputWrapper}>
                <TextField
                    value={description}
                    label="Description"
                    multiline
                    minRows={6}
                    maxRows={10}
                    sx={sx.textInput}
                    variant="outlined"
                    onChange={setCodeProperty('symbol_description')}
                    slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { maxLength: 400 },
                        input: {
                            endAdornment: (
                                <Info>
                                    <div>
                                        A detailed description of your code. This will appear on the "All Codes" screen
                                        on the charting page.
                                    </div>
                                </Info>
                            ),
                        },
                    }}
                />
            </div>

            <div style={sx.textInputWrapper}>
                <FormLabel component="legend">
                    Attach to:
                    <Info sx={{ fontSize: '16px', marginLeft: '4px' }}>
                        <div>
                            Codes must be attached to either a tooth or the mouth. Tooth codes <i>must</i> be attached
                            to a tooth, while mouth codes can be placed anywhere and will not be tied to teeth if placed
                            over a tooth.
                        </div>
                    </Info>
                </FormLabel>
                <RadioGroup value={mapTo} onChange={setCodeProperty('map_to')}>
                    <FormControlLabel value="tooth" control={<Radio />} label="Tooth" />
                    <FormControlLabel value="mouth" control={<Radio />} label="Mouth" />
                </RadioGroup>
            </div>

            <div>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => dispatch(actions.handleSimpleCodeSave())}
                    disabled={saving}
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

export default SimpleCode;
